/* eg-globe.js. Drives the "where it ships" globe in snippets/eg-v2-globe.liquid.
   Depends on eg-cobe.js having defined window.createGlobe.

   Notes on the cobe v2 API, because it differs from every v1 example online and
   the difference is not obvious from the docs:
   - v2 has NO internal animation loop and NO onRender callback. createGlobe()
     draws exactly one frame; every subsequent frame is a call to update().
     So the requestAnimationFrame loop below is ours to own, which is convenient:
     stopping it is genuinely free rather than just ignored.
   - update() MERGES partial state, so we only pass what changed.
   - width/height are CSS pixels. cobe multiplies them by devicePixelRatio
     itself. Passing device pixels here double-scales and you get a globe
     rendered at 4x into a quarter of the canvas.
   - When WebGL is unavailable cobe returns no-op update/destroy functions
     rather than throwing, so a try/catch cannot detect it. We probe first.

   Deliberate choices:
   - Nothing initialises until the section is on screen, and the loop stops when
     it leaves. The home page already carries a hero video; a WebGL context
     spinning for a visitor who never scrolls is pure cost.
   - The figures, the flag wall and the alt text are server-rendered; the canvas is
     decorative. If WebGL is missing or blocked we hide the canvas and the section
     still states every fact it exists to state.
   - prefers-reduced-motion draws one static frame and never starts the loop.
   - Coordinates come from a JSON tag written by ops/scripts/gen-shipped-to.py.
     City-level and rounded, so they are decoration, not addresses. */
(function () {
  'use strict';

  var root = document.querySelector('[data-eg-globe]');
  if (!root) return;

  var stage = root.querySelector('.egfw-stage');
  var canvas = root.querySelector('.egfw-canvas');
  var dataEl = document.getElementById('egg-pins');
  if (!stage || !canvas || !dataEl) return;

  function bail() { root.classList.add('egg-nogl'); }

  var pins;
  try {
    pins = JSON.parse(dataEl.textContent);
  } catch (e) {
    return bail();
  }
  if (!pins || !pins.length || !window.createGlobe) return bail();

  /* Probe on a throwaway canvas: cobe fails silently, so this is the only
     honest way to know whether we are about to render nothing. */
  try {
    var probe = document.createElement('canvas');
    if (!(probe.getContext('webgl2') || probe.getContext('webgl'))) return bail();
  } catch (e) {
    return bail();
  }

  /* Light treatment: the section sits on --eg-paper since layout H. A pale sphere
     with brass pins reads on that ground; the old navy sphere disappeared into it.
     If the section ever goes dark again, this whole block flips (see LIGHT below). */
  var PALE = [0.87, 0.875, 0.89];
  var BRASS = [0.761, 0.541, 0.235];  /* #c28a3c, --eg-accent */
  var GLOW = [0.95, 0.945, 0.935];

  var reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduced = !!(reducedQuery && reducedQuery.matches);

  var globe = null;
  var raf = null;
  var running = false;
  var phi = 4.1;      /* opens over the Atlantic, so US and European pins are both in view */
  var theta = 0.28;
  var size = 0;

  var dragX = null;
  var phiAtDown = 0;

  var markers = pins.map(function (p) {
    return { location: [p[0], p[1]], size: 0.042 };
  });

  function measure() {
    size = Math.round(stage.getBoundingClientRect().width) || 0;
    return size;
  }

  function build() {
    if (globe) return true;
    if (!measure()) return false;

    globe = window.createGlobe(canvas, {
      devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      width: size,
      height: size,
      phi: phi,
      theta: theta,
      dark: 0,            /* LIGHT: 0 = lit sphere for a paper ground, 1 = dark */
      diffuse: 1.9,
      mapSamples: 15000,
      mapBrightness: 2.9,
      baseColor: PALE,
      markerColor: BRASS,
      glowColor: GLOW,
      opacity: 1,
      markers: markers
    });

    root.classList.add('egg-ready');
    return true;
  }

  function frame() {
    if (!running || !globe) return;
    if (dragX === null) phi += 0.0022;
    globe.update({ phi: phi, theta: theta });
    raf = requestAnimationFrame(frame);
  }

  function start() {
    if (!build() || running) return;
    if (reduced) { globe.update({ phi: phi, theta: theta }); return; }
    running = true;
    raf = requestAnimationFrame(frame);
  }

  function stop() {
    running = false;
    if (raf) { cancelAnimationFrame(raf); raf = null; }
  }

  var onscreen = false;

  new IntersectionObserver(function (entries) {
    onscreen = entries[0].isIntersecting;
    if (onscreen && !document.hidden) start(); else stop();
  }, { rootMargin: '200px 0px' }).observe(root);

  /* A backgrounded tab already throttles rAF, but this also covers the visitor
     who leaves the tab open on this section for an hour. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stop();
    else if (onscreen) start();
  });

  if (reducedQuery && reducedQuery.addEventListener) {
    reducedQuery.addEventListener('change', function (e) {
      reduced = e.matches;
      if (reduced) stop(); else if (onscreen && !document.hidden) start();
    });
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (!globe) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (!measure()) return;
      globe.update({ width: size, height: size, phi: phi, theta: theta });
    }, 150);
  });

  /* Drag to spin, so a visitor can turn the globe to find their own city.
     Deliberately no inertia and no zoom: this is a proof element, not a map. */
  canvas.addEventListener('pointerdown', function (e) {
    dragX = e.clientX;
    phiAtDown = phi;
    try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
    root.classList.add('egg-dragging');
    if (reduced && globe) globe.update({ phi: phi, theta: theta });
  });

  canvas.addEventListener('pointermove', function (e) {
    if (dragX === null) return;
    phi = phiAtDown + (e.clientX - dragX) / 180;
    /* Under reduced motion the loop is not running, so draw the dragged frame. */
    if (reduced && globe) globe.update({ phi: phi, theta: theta });
  });

  function endDrag(e) {
    if (dragX === null) return;
    dragX = null;
    root.classList.remove('egg-dragging');
    if (e && e.pointerId != null) {
      try { canvas.releasePointerCapture(e.pointerId); } catch (err) {}
    }
  }
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);
})();
